// Content editors for each element type. Each receives the element, `update`
// (patch object or function, optional coalesce key) and the shared context
// (palette, fields, labels, kinds, formats, assets, upload).

import React from "react";
import FeatherIcon from "feather-icons-react";
import { Section, Field, NumberInput, SelectInput, Segmented, Toggle, ColorInput, TextInput, IconButton } from "../controls";
import RunsEditor from "../RunsEditor";
import StyleEditor from "../StyleEditor";
import { newId, LIMITS, lineFields } from "../designModel";

const move = (list, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= list.length) return list;
  const out = [...list];
  const [x] = out.splice(i, 1);
  out.splice(j, 0, x);
  return out;
};

function RowTools({ index, count, onMove, onDelete, label }) {
  return (
    <div className="td-rowtools">
      <IconButton icon="arrow-up" label={`Move ${label} up`} size={13} disabled={index === 0} onClick={() => onMove(-1)} />
      <IconButton icon="arrow-down" label={`Move ${label} down`} size={13} disabled={index === count - 1} onClick={() => onMove(1)} />
      <IconButton icon="trash-2" label={`Remove ${label}`} size={13} danger onClick={onDelete} />
    </div>
  );
}

const BORDER_OPTIONS = [
  { value: "grid", label: "Grid" },
  { value: "rows", label: "Rows" },
  { value: "outer", label: "Outline" },
  { value: "none", label: "None" },
];

function runsProps(ctx) {
  return {
    fieldGroups: ctx.docFieldGroups,
    labels: ctx.labels,
    kinds: ctx.kinds,
    formats: ctx.formats,
    palette: ctx.palette,
  };
}

// ---------------------------------------------------------------- text

export function TextPanel({ el, update, ctx, free, autoFocus }) {
  return (
    <>
      <Section title="Text">
        <RunsEditor {...runsProps(ctx)} runs={el.runs} autoFocus={autoFocus} rows={4} coalesceKey="runs" onChange={(runs, ck) => update({ runs }, ck)} />
      </Section>
      <StyleEditor style={el.style} palette={ctx.palette} valign={free} onChange={(style) => update({ style }, "style")} />
    </>
  );
}

// ---------------------------------------------------------------- key/value

export function KeyValuePanel({ el, update, ctx, free }) {
  const setRows = (rows, ck) => update({ rows }, ck);
  return (
    <>
      <Section title="Details rows">
        {el.rows.map((r, i) => (
          <div key={r.id} className="td-card">
            <div className="td-card__head">
              <span className="td-label">Row {i + 1}</span>
              <RowTools
                index={i}
                count={el.rows.length}
                label="row"
                onMove={(d) => setRows(move(el.rows, i, d))}
                onDelete={() => setRows(el.rows.filter((x) => x.id !== r.id))}
              />
            </div>
            <RunsEditor
              {...runsProps(ctx)}
              label="Label"
              rows={1}
              runs={r.label}
              coalesceKey={`kv-l-${r.id}`}
              onChange={(label, ck) => setRows(el.rows.map((x) => (x.id === r.id ? { ...x, label } : x)), ck)}
            />
            <RunsEditor
              {...runsProps(ctx)}
              label="Value"
              rows={1}
              runs={r.value}
              coalesceKey={`kv-v-${r.id}`}
              onChange={(value, ck) => setRows(el.rows.map((x) => (x.id === r.id ? { ...x, value } : x)), ck)}
            />
          </div>
        ))}
        <button
          type="button"
          className="td-btn td-btn--ghost td-btn--sm"
          disabled={el.rows.length >= LIMITS.kvRows}
          onClick={() => setRows([...el.rows, { id: newId("kv"), label: [{ text: "Label:", bold: true }], value: [{ text: "" }] }])}
        >
          <FeatherIcon icon="plus" size={13} /> Add row
        </button>
        <div className="td-grid2">
          <Field label="Label column">
            <NumberInput value={el.labelWidthPct} min={10} max={90} unit="%" onChange={(v) => update({ labelWidthPct: v }, "kvw")} />
          </Field>
          <Field label="Row spacing">
            <NumberInput value={el.rowGap} min={0} max={20} step={0.5} unit="mm" onChange={(v) => update({ rowGap: v }, "kvg")} />
          </Field>
        </div>
      </Section>
      <StyleEditor title="Label style" box={false} style={el.labelStyle} palette={ctx.palette} onChange={(s) => update({ labelStyle: s }, "kvls")} />
      <StyleEditor title="Value style" box={false} style={el.valueStyle} palette={ctx.palette} onChange={(s) => update({ valueStyle: s }, "kvvs")} />
      <StyleEditor title="Whole block" style={el.style} palette={ctx.palette} valign={free} onChange={(style) => update({ style }, "style")} />
    </>
  );
}

// ---------------------------------------------------------------- line table

export function TablePanel({ el, update, ctx }) {
  const fields = lineFields(ctx.fieldGroups);
  const setCols = (columns, ck) => update({ columns }, ck);
  const totalWidth = el.columns.reduce((a, c) => a + (Number(c.widthPct) || 0), 0);
  return (
    <>
      <Section title="Columns">
        <div className="td-hint">Widths are shares of the table ({Math.round(totalWidth)} in total), so they don't need to add up to 100.</div>
        {el.columns.map((c, i) => {
          const kind = ctx.kinds[c.field];
          const fmts =
            kind === "date" ? ctx.formats.dateFormats : kind === "money" ? ctx.formats.moneyFormats : kind === "number" ? ctx.formats.numberFormats : null;
          const setCol = (patch, ck) => setCols(el.columns.map((x) => (x.id === c.id ? { ...x, ...patch } : x)), ck);
          return (
            <div key={c.id} className="td-card">
              <div className="td-card__head">
                <span className="td-label">Column {i + 1}</span>
                <RowTools
                  index={i}
                  count={el.columns.length}
                  label="column"
                  onMove={(d) => setCols(move(el.columns, i, d))}
                  onDelete={() => setCols(el.columns.filter((x) => x.id !== c.id))}
                />
              </div>
              <Field label="Heading">
                <TextInput value={c.label} maxLength={60} onChange={(v) => setCol({ label: v }, `col-l-${c.id}`)} />
              </Field>
              <Field label="Shows">
                <SelectInput
                  value={c.field}
                  options={fields.map((fd) => ({ value: fd.key, label: fd.label }))}
                  onChange={(v) => setCol({ field: v, format: undefined })}
                />
              </Field>
              <div className="td-grid2">
                <Field label="Width">
                  <NumberInput value={c.widthPct} min={3} max={100} onChange={(v) => setCol({ widthPct: v }, `col-w-${c.id}`)} />
                </Field>
                <Field label="Align">
                  <Segmented
                    ariaLabel="Column alignment"
                    value={c.align}
                    onChange={(v) => setCol({ align: v || "left" })}
                    options={[
                      { value: "left", label: "Left", icon: "align-left" },
                      { value: "center", label: "Centre", icon: "align-center" },
                      { value: "right", label: "Right", icon: "align-right" },
                    ]}
                  />
                </Field>
              </div>
              {fmts && (
                <Field label="Format">
                  <SelectInput
                    value={c.format || ""}
                    placeholder="Default"
                    options={fmts.map((o) => ({ value: o.id, label: o.label }))}
                    onChange={(v) => setCol({ format: v || undefined })}
                  />
                </Field>
              )}
            </div>
          );
        })}
        <button
          type="button"
          className="td-btn td-btn--ghost td-btn--sm"
          disabled={el.columns.length >= LIMITS.tableColumns}
          onClick={() => setCols([...el.columns, { id: newId("col"), label: "Comment", field: "line.comment", widthPct: 15, align: "left" }])}
        >
          <FeatherIcon icon="plus" size={13} /> Add column
        </button>
      </Section>
      <Section title="Table options">
        <Field label="Borders">
          <Segmented ariaLabel="Table borders" value={el.borders} onChange={(v) => update({ borders: v || "grid" })} options={BORDER_OPTIONS} />
        </Field>
        {el.borders !== "none" && (
          <Field label="Border colour">
            <ColorInput value={el.borderColor} palette={ctx.palette} onChange={(v) => update({ borderColor: v })} />
          </Field>
        )}
        <Field label="Striped rows">
          <ColorInput value={el.stripe} palette={ctx.palette} onChange={(v) => update({ stripe: v })} />
        </Field>
        <Field label="Minimum rows" hint="Blank rows are added below the items up to this count, like a printed form.">
          <NumberInput value={el.fillerRows} min={0} max={30} onChange={(v) => update({ fillerRows: v || 0 })} />
        </Field>
        <Toggle label="Group lines by property" checked={el.groupByBuilding} onChange={(v) => update({ groupByBuilding: v })} />
        <Toggle label='Money as "$   1,234.50" (spreadsheet style)' checked={el.splitCurrency} onChange={(v) => update({ splitCurrency: v })} />
      </Section>
      <StyleEditor title="Header row" style={el.headerStyle} palette={ctx.palette} onChange={(s) => update({ headerStyle: s }, "hs")} boxOpen />
      <StyleEditor title="Item rows" style={el.rowStyle} palette={ctx.palette} onChange={(s) => update({ rowStyle: s }, "rs")} />
      <StyleEditor title="Whole table" typography={false} style={el.style} palette={ctx.palette} onChange={(style) => update({ style }, "style")} />
    </>
  );
}

// ---------------------------------------------------------------- totals

const TOTAL_KEYS = [
  { value: "subtotal", label: "Subtotal" },
  { value: "gst", label: "GST" },
  { value: "materials", label: "Materials" },
  { value: "total", label: "Total" },
];

export function TotalsPanel({ el, update, ctx, free }) {
  const setRows = (rows, ck) => update({ rows }, ck);
  return (
    <>
      <Section title="Totals rows">
        {el.rows.map((r, i) => {
          const setRow = (patch, ck) => setRows(el.rows.map((x) => (x.id === r.id ? { ...x, ...patch } : x)), ck);
          const st = r.style || {};
          const setSt = (p) => {
            const next = { ...st, ...p };
            Object.keys(next).forEach((k) => next[k] === undefined && delete next[k]);
            setRow({ style: Object.keys(next).length ? next : undefined });
          };
          return (
            <div key={r.id} className="td-card">
              <div className="td-card__head">
                <SelectInput ariaLabel="Amount shown" value={r.key} options={TOTAL_KEYS} onChange={(v) => setRow({ key: v })} />
                <RowTools index={i} count={el.rows.length} label="row" onMove={(d) => setRows(move(el.rows, i, d))} onDelete={() => setRows(el.rows.filter((x) => x.id !== r.id))} />
              </div>
              <RunsEditor {...runsProps(ctx)} label="Label" rows={1} runs={r.label} coalesceKey={`tr-${r.id}`} onChange={(label, ck) => setRow({ label }, ck)} />
              <div className="td-row-gap">
                <Toggle label="Bold" checked={st.fontWeight === 700} onChange={(v) => setSt({ fontWeight: v ? 700 : undefined })} />
              </div>
              <Field label="Row background">
                <ColorInput value={st.background} palette={ctx.palette} onChange={(v) => setSt({ background: v })} />
              </Field>
              <Field label="Row text colour">
                <ColorInput value={st.color} palette={ctx.palette} onChange={(v) => setSt({ color: v })} />
              </Field>
            </div>
          );
        })}
        <button
          type="button"
          className="td-btn td-btn--ghost td-btn--sm"
          disabled={el.rows.length >= LIMITS.totalsRows}
          onClick={() => setRows([...el.rows, { id: newId("tr"), key: "materials", label: [{ text: "Materials" }] }])}
        >
          <FeatherIcon icon="plus" size={13} /> Add row
        </button>
      </Section>
      <Section title="Totals options">
        <Field label="Borders">
          <Segmented ariaLabel="Totals borders" value={el.borders} onChange={(v) => update({ borders: v || "none" })} options={BORDER_OPTIONS} />
        </Field>
        {el.borders !== "none" && (
          <Field label="Border colour">
            <ColorInput value={el.borderColor} palette={ctx.palette} onChange={(v) => update({ borderColor: v })} />
          </Field>
        )}
        <Field label="Label column">
          <NumberInput value={el.labelWidthPct} min={10} max={90} unit="%" onChange={(v) => update({ labelWidthPct: v }, "tlw")} />
        </Field>
        <Toggle label='Money as "$   1,234.50" (spreadsheet style)' checked={el.splitCurrency} onChange={(v) => update({ splitCurrency: v })} />
      </Section>
      <StyleEditor title="Whole block" style={el.style} palette={ctx.palette} valign={free} onChange={(style) => update({ style }, "style")} />
    </>
  );
}

// ---------------------------------------------------------------- image

export function ImagePanel({ el, update, ctx }) {
  return (
    <>
      <Section title="Image">
        <Field label="Shows">
          <SelectInput
            value={el.source === "asset" ? el.assetId || "" : "vendorLogo"}
            options={[
              { value: "vendorLogo", label: "My company logo (from my profile)" },
              ...ctx.assets.map((a) => ({ value: a.id, label: a.filename || a.id })),
            ]}
            onChange={(v) => update(v === "vendorLogo" ? { source: "vendorLogo", assetId: undefined } : { source: "asset", assetId: v })}
          />
        </Field>
        <label className="td-btn td-btn--ghost td-btn--sm td-upload">
          <FeatherIcon icon="upload" size={13} /> Upload an image
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file) return;
              const asset = await ctx.onUpload(file);
              if (asset) update({ source: "asset", assetId: asset.id });
            }}
          />
        </label>
        <Field label="Fit">
          <Segmented
            ariaLabel="Image fit"
            value={el.fit}
            onChange={(v) => update({ fit: v || "contain" })}
            options={[
              { value: "contain", label: "Whole image" },
              { value: "cover", label: "Fill the box" },
            ]}
          />
        </Field>
      </Section>
      <StyleEditor title="Box" typography={false} style={el.style} palette={ctx.palette} onChange={(style) => update({ style }, "style")} boxOpen />
    </>
  );
}

// ---------------------------------------------------------------- shape

export function ShapePanel({ el, update, ctx }) {
  return (
    <Section title="Shape">
      <Field label="Shape">
        <Segmented
          ariaLabel="Shape"
          value={el.shape}
          onChange={(v) => update({ shape: v || "rect" })}
          options={[
            { value: "rect", label: "Rectangle", icon: "square" },
            { value: "ellipse", label: "Ellipse", icon: "circle" },
            { value: "line", label: "Line", icon: "minus" },
          ]}
        />
      </Field>
      {el.shape !== "line" && (
        <Field label="Fill">
          <ColorInput value={el.fill} palette={ctx.palette} onChange={(v) => update({ fill: v })} />
        </Field>
      )}
      <Field label={el.shape === "line" ? "Line colour" : "Outline"}>
        <ColorInput value={el.stroke} palette={ctx.palette} onChange={(v) => update({ stroke: v })} />
      </Field>
      <Field label={el.shape === "line" ? "Thickness" : "Outline width"}>
        <NumberInput value={el.strokeWidth} min={0} max={10} step={0.5} unit="px" onChange={(v) => update({ strokeWidth: v || 0 }, "sw")} />
      </Field>
      <Field label="Opacity">
        <NumberInput
          value={el.style?.opacity === undefined ? 100 : Math.round(el.style.opacity * 100)}
          min={0}
          max={100}
          step={5}
          unit="%"
          onChange={(v) => update((e) => ({ ...e, style: { ...(e.style || {}), opacity: v === 100 ? undefined : v / 100 } }), "op")}
        />
      </Field>
    </Section>
  );
}

// ---------------------------------------------------------------- simple blocks

export function PropertiesListPanel({ el, update, ctx, free }) {
  return (
    <>
      <Section title="Properties list">
        <div className="td-hint">Lists the properties on the invoice's lines, e.g. "Oak Tower, Spruce Heights".</div>
        <Field label="Separate with">
          <Segmented
            ariaLabel="Separator"
            value={el.separator}
            onChange={(v) => update({ separator: v || "comma" })}
            options={[
              { value: "comma", label: "Commas" },
              { value: "newline", label: "New lines" },
            ]}
          />
        </Field>
        <Toggle label="Include unit numbers" checked={el.withUnits} onChange={(v) => update({ withUnits: v })} />
      </Section>
      <StyleEditor style={el.style} palette={ctx.palette} valign={free} onChange={(style) => update({ style }, "style")} />
    </>
  );
}

export function RowPanel({ el, update, ctx }) {
  const setCols = (columns, ck) => update({ columns }, ck);
  return (
    <>
      <Section title="Columns">
        <div className="td-hint">Put elements side by side. Drag elements into a column on the page, or use "Move to section".</div>
        {el.columns.map((c, i) => (
          <div key={c.id} className="td-inline">
            <span className="td-label">Column {i + 1}</span>
            <NumberInput ariaLabel={`Column ${i + 1} width`} value={c.widthPct} min={5} max={100} unit="%" onChange={(v) => setCols(el.columns.map((x) => (x.id === c.id ? { ...x, widthPct: v } : x)), `rcw-${c.id}`)} />
            <span className="td-hint">{c.elements.length} item{c.elements.length === 1 ? "" : "s"}</span>
            <IconButton
              icon="trash-2"
              label={`Remove column ${i + 1}`}
              danger
              size={13}
              disabled={el.columns.length <= 1}
              onClick={() => {
                // Keep the column's contents: they move to the neighbouring column.
                const others = el.columns.filter((x) => x.id !== c.id);
                const target = others[Math.max(0, i - 1)];
                setCols(others.map((x) => (x.id === target.id ? { ...x, elements: [...x.elements, ...c.elements] } : x)));
              }}
            />
          </div>
        ))}
        <button
          type="button"
          className="td-btn td-btn--ghost td-btn--sm"
          disabled={el.columns.length >= LIMITS.rowColumns}
          onClick={() => setCols([...el.columns, { id: newId("rc"), widthPct: 50, elements: [] }])}
        >
          <FeatherIcon icon="plus" size={13} /> Add column
        </button>
        <Field label="Gap between columns">
          <NumberInput value={el.gap} min={0} max={40} step={0.5} unit="mm" onChange={(v) => update({ gap: v }, "gap")} />
        </Field>
      </Section>
      <StyleEditor title="Columns block" style={el.style} palette={ctx.palette} onChange={(style) => update({ style }, "style")} />
    </>
  );
}

export function DividerPanel({ el, update, ctx }) {
  return (
    <Section title="Divider">
      <Field label="Thickness">
        <NumberInput value={el.thickness} min={0.25} max={6} step={0.25} unit="px" onChange={(v) => update({ thickness: v }, "th")} />
      </Field>
      <Field label="Colour">
        <ColorInput value={el.color} palette={ctx.palette} onChange={(v) => update({ color: v })} />
      </Field>
    </Section>
  );
}

export function SpacerPanel({ el, update }) {
  return (
    <Section title="Spacer">
      <Field label="Height">
        <NumberInput value={el.height} min={1} max={150} step={0.5} unit="mm" onChange={(v) => update({ height: v }, "sp")} />
      </Field>
    </Section>
  );
}
